"use strict";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { AnimateTransition, Flex, LabeledCheckbox, Text, useSporeColors } from "ui/src";
import { BlockaidLogo } from "ui/src/components/logos/BlockaidLogo";
import { Modal } from "components/modals/Modal";
import { WarningModalContent } from "components/modals/WarningModal/WarningModal";
import { getAlertColor } from "components/modals/WarningModal/getAlertColor";
import { WarningSeverity } from "components/modals/WarningModal/types";
import { LearnMoreLink } from "components/text/LearnMoreLink";
import WarningIcon from "components/warnings/WarningIcon";
import { uniswapUrls } from "constants/urls";
import Trace from "features/telemetry/Trace";
import { ModalName } from "features/telemetry/constants";
import { TokenWarningFlagsTable } from "features/tokens/TokenWarningFlagsTable";
import { useBlockaidFeeComparisonAnalytics } from "features/tokens/hooks/useBlockaidFeeComparisonAnalytics";
import {
  TokenProtectionWarning,
  getCurrencyFeeOnTransfer,
  getFeeWarning,
  getIsFeeRelatedWarning,
  getSeverityFromTokenProtectionWarning,
  getShouldHaveCombinedPluralTreatment,
  getTokenProtectionFeeOnTransfer,
  getTokenProtectionWarning,
  getTokenWarningSeverity,
  useModalHeaderText,
  useModalSubtitleText
} from "features/tokens/safetyUtils";
import { useDismissedTokenWarnings } from "features/tokens/slice/hooks";
import { currencyIdToAddress } from "utils/uniswapCurrencyId";
function TokenWarningModalContent({
  currencyInfo0,
  currencyInfo1,
  isInfoOnlyWarning,
  onRejectButton,
  onAcknowledgeButton,
  shouldBeCombinedPlural,
  hasSecondWarning,
  feeOnTransferOverride,
  onDismissTokenWarning0,
  onDismissTokenWarning1
}) {
  const { t } = useTranslation();
  useBlockaidFeeComparisonAnalytics(currencyInfo0);
  const tokenProtectionWarning = feeOnTransferOverride?.buyFeePercent || feeOnTransferOverride?.sellFeePercent ? getFeeWarning(Math.max(feeOnTransferOverride.buyFeePercent ?? 0, feeOnTransferOverride.sellFeePercent ?? 0)) : getTokenProtectionWarning(currencyInfo0);
  const severity = getSeverityFromTokenProtectionWarning(tokenProtectionWarning);
  const tokenSymbol = currencyInfo0.currency.symbol;
  const isFeeRelatedWarning = getIsFeeRelatedWarning(tokenProtectionWarning);
  const { buyFeePercent: currencyBuyFeePercent, sellFeePercent: currencySellFeePercent } = getCurrencyFeeOnTransfer(
    currencyInfo0.currency
  );
  const { buyFeePercent, sellFeePercent } = getTokenProtectionFeeOnTransfer(currencyInfo0);
  const blockaidFeesData = currencyInfo0.safetyInfo?.blockaidFees;
  const showBlockaidFeesData = isFeeRelatedWarning && blockaidFeesData && (blockaidFeesData.buyFeePercent && (feeOnTransferOverride?.buyFeePercent ?? currencyBuyFeePercent) === void 0 || blockaidFeesData.sellFeePercent && (feeOnTransferOverride?.sellFeePercent ?? currencySellFeePercent) === void 0);
  const displayedBuyFeePercent = feeOnTransferOverride?.buyFeePercent ?? buyFeePercent;
  const displayedSellFeePercent = feeOnTransferOverride?.sellFeePercent ?? sellFeePercent;
  const showBlockaidLogo = !isFeeRelatedWarning && severity !== WarningSeverity.Low && severity !== WarningSeverity.Blocked || showBlockaidFeesData;
  const titleText = useModalHeaderText({
    tokenSymbol0: tokenSymbol,
    tokenSymbol1: currencyInfo1?.currency.symbol,
    tokenProtectionWarning,
    shouldHavePluralTreatment: shouldBeCombinedPlural
  });
  const subtitleText = useModalSubtitleText({
    tokenProtectionWarning,
    tokenSymbol,
    buyFeePercent: displayedBuyFeePercent,
    sellFeePercent: displayedSellFeePercent,
    shouldHavePluralTreatment: shouldBeCombinedPlural
  });
  const { headerText: titleTextColor } = getAlertColor(severity);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const showCheckbox = !isInfoOnlyWarning && severity === WarningSeverity.Low;
  const onAcknowledge = () => {
    if (showCheckbox) {
      if (dontShowAgain) {
        onDismissTokenWarning0();
        onDismissTokenWarning1?.();
      }
    }
    onAcknowledgeButton();
  };
  if (severity === WarningSeverity.None) {
    return null;
  }
  const { rejectText, acknowledgeText } = getWarningModalButtonTexts(
    t,
    !!isInfoOnlyWarning,
    severity,
    !!hasSecondWarning
  );
  const analyticsProperties = {
    tokenSymbol,
    tokenAddress: currencyIdToAddress(currencyInfo0.currencyId),
    chainId: currencyInfo0.currency.chainId,
    // if both tokens are low or blocked severities, their warnings are combined into 1 plural screen
    tokenSymbol1: currencyInfo1?.currency.symbol,
    tokenAddress1: currencyInfo1 && currencyIdToAddress(currencyInfo1.currencyId),
    warningSeverity: WarningSeverity[severity],
    tokenProtectionWarning: TokenProtectionWarning[tokenProtectionWarning],
    buyFeePercent: displayedBuyFeePercent,
    sellFeePercent: displayedSellFeePercent,
    safetyInfo: currencyInfo0.safetyInfo,
    ...showCheckbox && { dismissTokenWarningCheckbox: dontShowAgain }
  };
  return <Trace logImpression modal={ModalName.TokenWarningModal} properties={analyticsProperties}>
      <Flex>
        <WarningModalContent
    modalName={ModalName.TokenWarningModal}
    captionComponent={<Text color="$neutral2" textAlign="center" variant="body3">
              {`${subtitleText} `}
              <LearnMoreLink
      display="inline"
      textColor="$neutral1"
      textVariant="buttonLabel3"
      url={uniswapUrls.helpArticleUrls.tokenWarning}
    />
            </Text>}
    rejectText={rejectText}
    acknowledgeText={acknowledgeText}
    icon={<WarningIcon heroIcon inModal severity={severity} size="$icon.24" />}
    backgroundIconColor={false}
    severity={severity}
    titleComponent={<Text color={titleTextColor} variant="subheading1">
              {titleText}
            </Text>}
    onReject={onRejectButton}
    onClose={onRejectButton}
    onAcknowledge={onAcknowledge}
  >
          {tokenProtectionWarning !== TokenProtectionWarning.NonDefault && <TokenWarningFlagsTable currencyInfo={currencyInfo0} tokenProtectionWarning={tokenProtectionWarning} />}

          {showBlockaidLogo && <Flex row centered>
              <Text variant="body3" color="$neutral3">
                <Trans
    i18nKey="common.poweredBy"
    components={{ name: <BlockaidLogo minHeight={10} minWidth={50} color="$neutral3" /> }}
  />
              </Text>
            </Flex>}

          {showCheckbox && // only show "Don't show this warning again" checkbox if this is an actionable modal & the token is low-severity
  <LabeledCheckbox
    checked={dontShowAgain}
    checkedColor="$neutral1"
    text={<Text color="$neutral2" variant="buttonLabel3">
                  {t("token.safety.warning.dontShowWarningAgain")}
                </Text>}
    size="$icon.16"
    gap="$spacing8"
    onCheckPressed={() => setDontShowAgain((s) => !s)}
  />}
        </WarningModalContent>
      </Flex>
    </Trace>;
}
function useWarningModalCurrenciesDismissed(t0, t1, isInfoOnlyWarning) {
  const address0 = currencyIdToAddress(t0.currencyId);
  const address1 = t1 && currencyIdToAddress(t1.currencyId);
  const { tokenWarningDismissed: tokenWarningDismissed0, onDismissTokenWarning: onDismissTokenWarning0 } = useDismissedTokenWarnings(t0?.currency.isNative ? void 0 : { chainId: t0.currency.chainId, address: address0 });
  const { tokenWarningDismissed: tokenWarningDismissed1, onDismissTokenWarning: onDismissTokenWarning1 } = useDismissedTokenWarnings(
    !t1 || !address1 || t1?.currency.isNative ? void 0 : { chainId: t1.currency.chainId, address: address1 }
  );
  let currencyInfo0 = t0;
  let currencyInfo1 = t1;
  if (!isInfoOnlyWarning) {
    if (tokenWarningDismissed0 && tokenWarningDismissed1) {
      return null;
    } else if (tokenWarningDismissed0) {
      if (!t1) {
        return null;
      }
      currencyInfo0 = t1 ?? void 0;
    } else if (tokenWarningDismissed1) {
      currencyInfo0 = t0;
      currencyInfo1 = void 0;
    }
  }
  return { currencyInfo0, onDismissTokenWarning0, currencyInfo1, onDismissTokenWarning1 };
}
export default function TokenWarningModal({
  isVisible,
  currencyInfo0: t0,
  currencyInfo1: t1,
  isInfoOnlyWarning,
  feeOnTransferOverride,
  onReject,
  onToken0BlockAcknowledged,
  onToken1BlockAcknowledged,
  onAcknowledge,
  closeModalOnly
}) {
  const colors = useSporeColors();
  const [warningIndex, setWarningIndex] = useState(0);
  const warningModalCurrencies = useWarningModalCurrenciesDismissed(t0, t1, isInfoOnlyWarning);
  if (!warningModalCurrencies) {
    return null;
  }
  const { currencyInfo0, currencyInfo1, onDismissTokenWarning0, onDismissTokenWarning1 } = warningModalCurrencies;
  const combinedPlural = getShouldHaveCombinedPluralTreatment(currencyInfo0, currencyInfo1);
  const isBlocked0 = getTokenWarningSeverity(currencyInfo0) === WarningSeverity.Blocked;
  const isBlocked1 = getTokenWarningSeverity(currencyInfo1) === WarningSeverity.Blocked;
  const hasSecondWarning = Boolean(!combinedPlural && getTokenWarningSeverity(currencyInfo1) !== WarningSeverity.None);
  return <Modal
    backgroundColor={colors.surface1.val}
    isModalOpen={isVisible}
    name={ModalName.TokenWarningModal}
    skipLogImpression={true}
    onClose={onReject ?? closeModalOnly}
  >
      {hasSecondWarning && <Flex row $sm={{ position: "absolute", top: -16 }}>
          <Text variant="body2">{warningIndex + 1}</Text>
          <Text color="$neutral2" variant="body2">
            {" / 2"}
          </Text>
        </Flex>}
      <AnimateTransition currentIndex={warningIndex} animationType={warningIndex === 0 ? "forward" : "backward"}>
        <TokenWarningModalContent
    currencyInfo0={currencyInfo0}
    currencyInfo1={currencyInfo1}
    isInfoOnlyWarning={!hasSecondWarning && isInfoOnlyWarning}
    hasSecondWarning={hasSecondWarning}
    shouldBeCombinedPlural={combinedPlural}
    feeOnTransferOverride={feeOnTransferOverride}
    onRejectButton={onReject ?? closeModalOnly}
    onAcknowledgeButton={() => {
      if (hasSecondWarning) {
        setWarningIndex(1);
      } else if (isBlocked0) {
        combinedPlural && isBlocked1 && onToken1BlockAcknowledged?.();
        onToken0BlockAcknowledged?.();
        closeModalOnly();
      } else if (isInfoOnlyWarning) {
        closeModalOnly();
      } else {
        onAcknowledge();
      }
    }}
    onDismissTokenWarning0={onDismissTokenWarning0}
    onDismissTokenWarning1={onDismissTokenWarning1}
  />
        {hasSecondWarning && currencyInfo1 && <TokenWarningModalContent
    hasSecondWarning
    currencyInfo0={currencyInfo1}
    onDismissTokenWarning0={onDismissTokenWarning1}
    onRejectButton={() => {
      setWarningIndex(0);
    }}
    onAcknowledgeButton={() => {
      if (isBlocked0 || isBlocked1) {
        isBlocked0 && onToken0BlockAcknowledged?.();
        isBlocked1 && onToken1BlockAcknowledged?.();
        closeModalOnly();
      } else {
        onAcknowledge();
      }
    }}
  />}
      </AnimateTransition>
    </Modal>;
}
export function getWarningModalButtonTexts(t, isInfoOnlyWarning, severity, hasSecondWarning) {
  if (isInfoOnlyWarning) {
    return {
      rejectText: t("common.button.close"),
      acknowledgeText: void 0
    };
  }
  if (severity === WarningSeverity.Blocked && !hasSecondWarning) {
    return {
      rejectText: void 0,
      acknowledgeText: t("common.button.close")
    };
  }
  return {
    rejectText: t("common.button.goBack"),
    acknowledgeText: t("common.button.continue")
  };
}
